/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ReactElement, useContext } from "react";
import moment from "moment";
import { Box } from "@mui/material";
import { Button, Badge } from "@chakra-ui/react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridClasses,
} from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import { Tooltip, IconButton } from "@mui/material";
import { AiOutlineFileSearch } from "react-icons/ai";
import { MdOutlineContentCopy } from "react-icons/md";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ProfileContext } from "../../../../store/ProfileProvider";

dayjs.extend(utc);
dayjs.extend(timezone);

type Merchant = {
  id: string;
  name: string;
};

type Balances = {
  available: string;
};

type RowData = {
  id: string;
  merchant: Merchant;
  suba_name: string;
  balances: Balances;
};

const AgentList: React.FC<{
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
  currentLastKey: string;
}> = ({
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
  currentLastKey,
}) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  const handleClickCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy ID: ", err);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "",
      flex: 1,
      minWidth: 50,
      maxWidth: 50,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams<RowData, any, string>) => (
        <Tooltip title="Copy ID" arrow placement="top">
          <div>
            <IconButton
              id={"button-copy-id-" + params?.row?.id}
              onClick={() => handleClickCopy(params.row.id)}
            >
              <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
            </IconButton>
          </div>
        </Tooltip>
      ),
    },
    {
      field: "merchant",
      headerName: "Merchant",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData, any, string>) =>
        params.row.merchant.name,
    },
    {
      field: "suba_name",
      headerName: "Agent",
      minWidth: 150,
      sortable: false,
      flex: 1,
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      flex: 1,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => {
        return (
          <Tooltip title="View More Details" arrow placement="right">
            <div className="mr-5">
              <IconButton
                component={Link}
                to={`/${userRole}/agents/${params.row.id}`}
                id={"button-detail-" + params?.row?.id}
              >
                <AiOutlineFileSearch className="h-5 w-5 text-navy-700" />
              </IconButton>
            </div>
          </Tooltip>
        );
      },
    },
  ];

  interface CustomerStatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const customDataGridStyles: Partial<GridClasses> = {
    root: "mui-table-custom",
    columnHeader: "text-start text-md",
    row: "",
  };

  return (
    <Box className="card mt-3 w-full">
      <DataGrid
        sx={{
          "& .MuiDataGrid-cell": {
            padding: "8px 16px",
          },
          "& .MuiDataGrid-columnHeader": {
            padding: "8px 16px",
          },
          "& .MuiTablePagination-input": {
            marginRight: "-20px",
          },
          "& .MuiTablePagination-displayedRows": {
            display: "none",
          },
        }}
        loading={isLoading}
        rows={tableData || []}
        columns={columns}
        getRowId={(row) => row.id}
        rowCount={rowCount}
        sortingMode="server"
        paginationMode="server"
        pageSizeOptions={[10, 25, 50, 100]}
        rowHeight={48}
        columnHeaderHeight={48}
        paginationModel={{ page: page, pageSize: pageSize }}
        onPaginationModelChange={(newValue) => setPaginationModel(newValue)}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        classes={customDataGridStyles}
        hideFooter={currentLastKey === "" && page === 0}
        autoHeight
        localeText={{
          noRowsLabel: "No data available",
        }}
      />
    </Box>
  );
};

export default AgentList;
